import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { ErrorAlert, formatTs } from '../components';
import type { Integration, IntegrationAccount, IntegrationCredentials, IntegrationProbe } from '../integrationTypes';
import CredentialFields, {
    CredentialField, credentialsBody, credentialsComplete, fieldErrors, ProbeResult, ProductionKeyWarnings,
} from './CredentialFields';
import { CREDENTIALS_MANAGED_BY, credentialState } from './integrationLabels';
import { useAction, useIntegrations } from './IntegrationsContext';
import { DiscardPrompt } from './parts';
import SettingsEditor from './SettingsEditor';

/** The contract's slug rule (AccountSlug): it goes into the vendor-side webhook URLs and never changes. */
export const SLUG_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;

const changedBy = (credentials: IntegrationCredentials) => {
    if (!credentials.changed_at) {
        return null;
    }
    const who = credentials.changed_by?.name;
    return `changed ${formatTs(credentials.changed_at)}${who ? ` by ${who}` : ''}`;
};

/** "Credentials set ✓ / missing", with an icon (never colour alone); an account with no credential group of its own
 *  says why instead ("Credentials on accounts", "No credentials"), with no tick. */
export const CredentialMark = ({ credentials }: { credentials: IntegrationCredentials }) => {
    const state = credentialState(credentials);
    const none = credentials.keys.length === 0;
    return (
        <Box
            component="span"
            data-testid="credential-mark"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, typography: 'body2', whiteSpace: 'nowrap', color: none ? 'text.secondary' : 'text.primary' }}
        >
            {!none && (state.ok
                ? <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} aria-hidden />
                : <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.dark' }} aria-hidden />)}
            {state.text}
        </Box>
    );
};

const fieldsOf = (credentials: IntegrationCredentials, withSet: boolean): CredentialField[] => credentials.keys.map((k) => ({
    key: k.key, label: k.label, secret: k.secret, required: k.required, set: withSet ? k.set : false,
}));

/** An account's credentials (plan §1 Account editor): what is set, and "Replace" -- the whole group, write-only,
 *  with [Check] against the vendor before saving. Saving never calls the vendor. */
export const CredentialsBlock = ({ connection, account, canEdit, onDirtyChange }: {
    connection: Integration;
    account: IntegrationAccount;
    canEdit: boolean;
    /** Something is typed and not saved (the account editor's Close asks first). */
    onDirtyChange?: (dirty: boolean) => void;
}) => {
    const { api, replace } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const credentials = account.credentials;
    const fields = fieldsOf(credentials, true);
    const [open, setOpen] = React.useState(false);
    const [values, setValues] = React.useState<Record<string, string>>({});
    const [probe, setProbe] = React.useState<IntegrationProbe | null>(null);
    const checking = useAction();
    const saving = useAction();
    const complete = credentialsComplete(fields, values);
    const typed = open && Object.values(values).some((v) => v !== '');
    React.useEffect(() => {
        onDirtyChange?.(typed);
    }, [typed, onDirtyChange]);
    const labels = credentials.keys.map((k) => k.label);
    const missing = credentials.keys.filter((k) => !k.set).map((k) => k.label);
    // Curated Planet's, or a Bókun app connection's (its app token): shown, never replaced here; Check still works.
    const managedBy = credentials.managed_by || 'client';

    const close = () => {
        setOpen(false);
        setValues({});
        setProbe(null);
        checking.setError(null);
        saving.setError(null);
    };
    const check = async () => {
        setProbe(null);
        const outcome = await checking.run(() => api.check(connection.id, { account: account.slug, credentials: credentialsBody(fields, values) }));
        if (outcome.ok) {
            setProbe(outcome.value);
        }
    };
    const save = async () => {
        const outcome = await saving.run(() => api.replaceCredentials(connection.id, credentialsBody(fields, values), account.slug));
        if (outcome.ok) {
            replace(outcome.value);
            close();
            enqueueSnackbar('Credentials saved', { variant: 'success' });
        }
    };

    return (
        <Box data-testid="credentials">
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
                <CredentialMark credentials={credentials} />
                <Typography variant="body2" color="text.secondary">
                    {credentials.complete ? labels.join(', ') : `Missing: ${missing.join(', ') || credentials.missing.join(', ')}`}
                    {changedBy(credentials) ? ` · ${changedBy(credentials)}` : ''}
                </Typography>
                {canEdit && account.editable && !open && fields.length > 0 && managedBy === 'client' && (
                    <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
                        {credentials.keys.some((k) => k.set) ? 'Replace credentials' : 'Set credentials'}
                    </Button>
                )}
            </Stack>
            {managedBy !== 'client' && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }} data-testid="credentials-managed-by">
                    {managedBy === 'vendor_app'
                        ? `Connected through the ${connection.name} app: change them there.`
                        : CREDENTIALS_MANAGED_BY[managedBy] || managedBy}
                </Typography>
            )}
            <ProductionKeyWarnings items={credentials.production_key} />
            {open && (
                <Box sx={{ mt: 1.5 }} data-testid="credentials-form">
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 1.5, maxWidth: '68ch' }}>
                        Stored values are never shown again. Type the whole set: it replaces the current one together.
                    </Typography>
                    <CredentialFields fields={fields} values={values} onChange={setValues} errors={fieldErrors(saving.error)} disabled={saving.busy} autoFocus />
                    {probe && <ProbeResult probe={probe} onClose={() => setProbe(null)} />}
                    {checking.error ? <ErrorAlert error={checking.error} /> : null}
                    {saving.error ? <ErrorAlert error={saving.error} /> : null}
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                        {connection.can_check && (
                            <Button size="small" variant="outlined" onClick={check} disabled={!complete || checking.busy || saving.busy} startIcon={checking.busy ? <CircularProgress size={14} /> : undefined}>
                                Check
                            </Button>
                        )}
                        <Button size="small" variant="contained" onClick={save} disabled={!complete || saving.busy} startIcon={saving.busy ? <CircularProgress size={14} /> : undefined}>
                            Save credentials
                        </Button>
                        <Button size="small" onClick={close} disabled={saving.busy}>Cancel</Button>
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

const PANEL = 'grey.50';

const Panel = ({ children, testId }: { children: React.ReactNode; testId: string }) => (
    // A tinted region under its row, not a card in the card.
    <Box sx={{ bgcolor: PANEL, borderRadius: 1, p: { xs: 1.5, sm: 2 }, mt: 1, mb: 1 }} data-testid={testId}>{children}</Box>
);

const NewAccount = ({ connection, onClose }: { connection: Integration; onClose: () => void }) => {
    const { api, replace } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const template = connection.accounts.find((a) => a.is_default) || connection.accounts[0];
    const fields = template ? fieldsOf(template.credentials, false) : [];
    const [slug, setSlug] = React.useState('');
    const [name, setName] = React.useState('');
    const [values, setValues] = React.useState<Record<string, string>>({});
    const [probe, setProbe] = React.useState<IntegrationProbe | null>(null);
    const checking = useAction();
    const adding = useAction();
    const typed = slug.trim();
    const taken = connection.accounts.some((a) => a.slug === typed);
    const slugError = !typed ? null : !SLUG_PATTERN.test(typed)
        ? 'Lowercase letters, digits, - and _ only, starting with a letter or a digit (at most 64).'
        : taken ? 'An account has this slug already.' : null;
    const errors = fieldErrors(adding.error);
    const ready = typed && !slugError && credentialsComplete(fields, values);

    const check = async () => {
        setProbe(null);
        const outcome = await checking.run(() => api.check(connection.id, { credentials: credentialsBody(fields, values) }));
        if (outcome.ok) {
            setProbe(outcome.value);
        }
    };
    const add = async () => {
        if (!ready) {
            return;
        }
        const outcome = await adding.run(() => api.addAccount(connection.id, {
            slug: typed,
            ...(name.trim() ? { display_name: name.trim() } : {}),
            credentials: credentialsBody(fields, values),
        }));
        if (outcome.ok) {
            replace(outcome.value);
            enqueueSnackbar(`Account ${name.trim() || typed} added`, { variant: 'success' });
            onClose();
        }
    };

    return (
        <Panel testId="new-account">
            {/* No <form>: vendor credentials must not read as a sign-up form to password managers. */}
            <Box>
                <Typography variant="subtitle2" component="h4" sx={{ fontWeight: 500, mb: 1.5 }}>New account</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5, mb: 2 }}>
                    <TextField
                        size="small"
                        label="Slug"
                        required
                        autoFocus
                        value={slug}
                        onChange={(ev) => setSlug(ev.target.value.toLowerCase())}
                        error={!!slugError || !!errors.slug}
                        helperText={slugError || errors.slug || `It goes into this account's webhook addresses and can never change, e.g. …/${connection.system}/lisbon/booking/.`}
                        inputProps={{ spellCheck: false, autoComplete: 'off', maxLength: 64, 'data-testid': 'account-slug' }}
                    />
                    <TextField
                        size="small"
                        label="Display name"
                        value={name}
                        onChange={(ev) => setName(ev.target.value)}
                        helperText="How the account shows here, e.g. Lisbon office."
                        inputProps={{ maxLength: 120, 'data-testid': 'account-name' }}
                    />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Its credentials at {connection.name}</Typography>
                <CredentialFields fields={fields} values={values} onChange={setValues} errors={errors} disabled={adding.busy} />
                {probe && <ProbeResult probe={probe} onClose={() => setProbe(null)} />}
                {checking.error ? <ErrorAlert error={checking.error} /> : null}
                {adding.error ? <ErrorAlert error={adding.error} /> : null}
                <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                    {connection.can_check && (
                        <Button size="small" variant="outlined" onClick={check} disabled={!credentialsComplete(fields, values) || checking.busy} startIcon={checking.busy ? <CircularProgress size={14} /> : undefined}>
                            Check
                        </Button>
                    )}
                    <Button size="small" variant="contained" onClick={add} disabled={!ready || adding.busy} startIcon={adding.busy ? <CircularProgress size={14} /> : undefined}>
                        Add account
                    </Button>
                    <Button size="small" onClick={onClose} disabled={adding.busy}>Cancel</Button>
                </Stack>
            </Box>
        </Panel>
    );
};

/** Inline under its row (plan §1): the account's credentials and, on a named account, its overrides of the
 *  connection's client-editable settings; `account` null adds a new one (slug, display name, credentials). */
const AccountEditor = ({ connection, account, canEdit, onClose }: {
    connection: Integration;
    account: IntegrationAccount | null;
    canEdit: boolean;
    onClose: () => void;
}) => {
    const { api, replace } = useIntegrations();
    const { enqueueSnackbar } = useSnackbar();
    const [typedCredentials, setTypedCredentials] = React.useState(false);
    const [overrides, setOverrides] = React.useState(0);
    const [asking, setAsking] = React.useState(false);
    if (!account) {
        return <NewAccount connection={connection} onClose={onClose} />;
    }
    // Close never drops what was typed silently (review I5 #12): it asks first.
    const close = () => {
        if (typedCredentials || overrides > 0) {
            setAsking(true);
        } else {
            onClose();
        }
    };
    const saveOverrides = async (patch: Record<string, any>) => {
        const updated = await api.saveSettings(connection.id, patch, account.slug);
        replace(updated);
        enqueueSnackbar(`Settings of ${account.display_name} saved`, { variant: 'success' });
    };
    return (
        <Panel testId={`account-editor-${account.slug || 'default'}`}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Credentials</Typography>
            <CredentialsBlock connection={connection} account={account} canEdit={canEdit} onDirtyChange={setTypedCredentials} />
            {account.settings && connection.settings && Object.keys(account.settings.values).length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Settings for this account</Typography>
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 0.5, maxWidth: '68ch' }}>
                        Each setting inherits the connection&apos;s value unless this account overrides it.
                    </Typography>
                    <SettingsEditor
                        schema={connection.settings.schema}
                        values={account.settings.values}
                        inherited={connection.settings.values}
                        level="account"
                        editable={canEdit && account.editable}
                        surface={PANEL}
                        onSave={saveOverrides}
                        onClose={() => undefined}
                        onDirtyChange={setOverrides}
                    />
                </Box>
            )}
            {asking && <DiscardPrompt onDiscard={onClose} onKeep={() => setAsking(false)} />}
            <Box sx={{ mt: 1.5 }}>
                <Button size="small" onClick={close}>Close</Button>
            </Box>
        </Panel>
    );
};

export default AccountEditor;
